import "./reset.css";
import "react-app-polyfill/stable";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { createGlobalStyle } from "styled-components";
import { MsalProvider } from "@azure/msal-react";
import { getMsalInstanceAsync } from "@coworker/app/src/core/auth/useAuth";
import { fixaColorScheme } from "@coworker/common/config/colorScheme";
import { i18n, I18nextProvider } from "@coworker/locales";
import { RouterProvider } from "react-router";
import { OopsScreen } from "@coworker/app/src/components/OopsScreen";
import versionJson from "@coworker/app/src/core/version.json";
import * as Sentry from "@sentry/react";
import { shouldReportToSentry } from "@coworker/common/helpers/sentry.helpers";
import { gaService } from "@coworker/common/services/google-analytics.service";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { router } from "@coworker/app/src/routes";
import { addSentryBreadcrumb } from "@coworker/app/src/hooks/useConfigureSentry";
import { queryClient } from "./queryClient";
import { StyledComponentsManager } from "./styledComponents";

const Global = createGlobalStyle`
  ${() => fixaColorScheme}

  @font-face {
    font-family: 'NotoSansIKEA';
    src: url(/fonts/NotoSansIKEA-Regular.woff2) format('woff2'), url(/fonts/NotoSansIKEA-Regular.woff) format('woff');
    font-display: block;
  }

  @font-face {
    font-family: 'NotoSansIKEA';
    src: url(/fonts/NotoSansIKEA-Bold.woff2) format('woff2'), url(/fonts/NotoSansIKEA-Bold.woff) format('woff');
    font-weight: bold;
    font-display: block;
  }

  @font-face {
    font-family: 'NotoSansIKEA';
    src: url(/fonts/NotoSansIKEA-Italic.woff2) format('woff2'), url(/fonts/NotoSansIKEA-Italic.woff) format('woff');
    font-style: italic;
    font-display: block;
  }

  @font-face {
    font-family: 'NotoSansIKEA';
    src: url(/fonts/NotoSansIKEA-BoldItalic.woff2) format('woff2'), url(/fonts/NotoSansIKEA-BoldItalic.woff) format('woff');
    font-weight: bold;
    font-style: italic;
    font-display: block;
  }

  html {
    background: var(--white);
  }

  body, html, #root {
    height: 100%;
    padding: 0;
    margin: 0;
    border: 0;
    box-shadow: none;
  }

  * {
    -webkit-tap-highlight-color: transparent;
  }

  html, body {
    overscroll-behavior-y: contain;
  }

  a {
    text-decoration: none;
  }

  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    background: var(--white);
  }

  ::-webkit-scrollbar-track {
    width: 5px;
    height: 5px;
    background: var(--white);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0;
  }

  div[data-testid="describeTheProblemWidget"] {
    user-select: auto;
  }
`;

const IS_CHINA = isChineseEnvironment();

const msalInstance = await getMsalInstanceAsync();

addSentryBreadcrumb("msal:handleRedirectPromise:start");

try {
  const redirectResult = await msalInstance.handleRedirectPromise();
  addSentryBreadcrumb("msal:handleRedirectPromise:end", {
    extra: { hasTokenResponse: !!redirectResult },
  });
  // Set the active account after the redirect has been processed so that
  // accounts populated from the redirect hash are available. This fixes a
  // race where useAuth.ts called setActiveAccount before handleRedirectPromise
  // had hydrated the cache, leaving the active account as null for the session.
  const account = redirectResult?.account ?? msalInstance.getAllAccounts()[0];
  if (account) {
    msalInstance.setActiveAccount(account);
  }
  try {
    sessionStorage.removeItem("msal.redirected");
  } catch {
    // Storage can be unavailable due to browser privacy settings.
  }
} catch (error) {
  addSentryBreadcrumb("msal:handleRedirectPromise:error", {
    extra: { error },
  });
  // Report as a real Sentry event (not just a breadcrumb) so failed redirects
  // are visible in alerting. Also clear the redirect guard so the next page
  // load can retry auth rather than silently returning null forever.
  Sentry.captureException(error, {
    tags: { area: "auth", flow: "handleRedirectPromise" },
  });
  try {
    sessionStorage.removeItem("msal.redirected");
  } catch {
    // Storage can be unavailable due to browser privacy settings.
  }
}

function AppContainer() {
  // This mitigates an old issue that we will never fully get rid of.
  // This should always be kept at the very top level and never removed.
  useEffect(() => {
    caches.delete("latest-index").catch((reason) => {
      console.error(`Failed to delete 'latest-index' cache: ${reason}`);
    });
  }, []);

  return (
    <StyledComponentsManager>
      <MsalProvider instance={msalInstance}>
        <I18nextProvider i18n={i18n}>
          <Global />
          <Sentry.ErrorBoundary fallback={(props) => <OopsScreen {...props} />}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </Sentry.ErrorBoundary>
        </I18nextProvider>
      </MsalProvider>
    </StyledComponentsManager>
  );
}

if (import.meta.env.VITE_APP_ENV === "prod") {
  gaService.init("G-TBK5FH8YF4");
}

window.PHRASEAPP_CONFIG = {
  projectId: "d17e83366cc489540d297fef05accd65",
  prefix: "[[__",
  suffix: "__]]",
  priorityLocales: ["en-GB"],
  fullReparse: true,
  autoLowercase: false,
};
const env = import.meta.env.VITE_APP_ENV;
const sentryDSN = IS_CHINA
  ? "https://5183c3d55f014a8aa06288be2364334c@sentry.ingka-system.cn/101"
  : "https://ccd29981faf3433891c93858c9cded01@o478484.ingest.sentry.io/5407140";
Sentry.init({
  dsn: sentryDSN,
  environment: env,
  maxValueLength: 13337,
  release: versionJson.version,
  enabled: IS_CHINA || shouldReportToSentry(env),
  attachStacktrace: true,
  // Same-origin gateway domains for all backend services across environments
  // (see packages/http-client/src/adapters/services.ts) - enables
  // sentry-trace/baggage header propagation on those requests. NOTE: this is
  // a top-level Sentry.init option, not a browserTracingIntegration option -
  // putting it under the integration is silently ignored (no headers sent).
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api(-dev|\.stage|\.dev)?\.fixa\.ingka\.com/,
    /^https:\/\/fixa-api(-dev)?\.ingka-(internal|dt)\.(com|cn)/,
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 0.1,
  // https://docs.sentry.io/platforms/javascript/guides/react/session-replay/
  replaysSessionSampleRate: 0.005,
  replaysOnErrorSampleRate: 0.1,
  ignoreErrors: [
    "Network Error",
    "deadline-exceeded",
    "ChunkLoadError",
    "x_content_parse_exception",
    "However, this document does exist",
    "Document already exists",
    "Error: Network Error",
    "Failed to get document because the client is offline.",
    "AbortError: The operation was aborted.",
    "You do not have your profile created.",
    "Uncaught NetworkError",
  ],
});

// Needed so Chrome < 71 (=a few months ago) does not crash when trying to create a task. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/globalThis https://dev.to/lampewebdev/stop-usuing-this-instead-use-globalthis-for-global-variables-2bc1 https://blog.logrocket.com/what-is-globalthis-why-use-it/
if (typeof globalThis === "undefined") window.globalThis = window;

const root = ReactDOM.createRoot(document.getElementById("root"));
// Wrapping in strict mode to catch potential issues to help with debugging
root.render(
  <StrictMode>
    <AppContainer />
  </StrictMode>
);
